import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { Description } from '../shared/Description'

const BANNER_IMAGE = '/assets/about_us/Nis Banner - Sight2Sight.jpeg'
const SEPARATOR_IMAGE = '/assets/about_us/Augsburg Banner - Sight2Sight.jpeg'

const BANNER_TITLES: Record<string, { name: string; description: string }> = {
  en: {
    name: 'About Us',
    description: 'Discover tours and activities worldwide',
  },
  de: {
    name: 'Über Uns',
    description: 'Entdecken Sie Touren und Aktivitäten weltweit',
  },
  es: {
    name: 'Sobre Nosotros',
    description: 'Descubre tours y actividades en todo el mundo',
  },
  nl: {
    name: 'Over Ons',
    description: 'Ontdek tours en activiteiten wereldwijd',
  },
  fr: {
    name: 'À Propos',
    description: 'Découvrez des tours et activités dans le monde entier',
  },
  it: {
    name: 'Chi Siamo',
    description: 'Scopri tour e attività in tutto il mondo',
  },
}

function buildRichText(
  key: string,
  text: string,
  style: 'h2' | 'normal' = 'normal',
): RichText {
  return {
    _key: key,
    _type: 'block',
    children: [{ _key: `${key}-span`, _type: 'span', marks: [], text }],
    markDefs: [],
    style,
  }
}

export function AboutUs() {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language as keyof typeof BANNER_TITLES
  const bannerTitle = BANNER_TITLES[locale] || BANNER_TITLES.en

  const content1: RichText[] = [
    buildRichText('about-1', t('about-title'), 'h2'),
    buildRichText('about-2', t('about-intro')),
    buildRichText('about-3', t('about-whatwedo-title'), 'h2'),
    buildRichText('about-4', t('about-whatwedo-text')),
  ]

  const content2: RichText[] = [
    buildRichText('about-5', t('about-team-title'), 'h2'),
    buildRichText('about-6', t('about-team-text-1')),
    buildRichText('about-6b', t('about-team-text-2')),
    buildRichText('about-7', t('about-partners-title'), 'h2'),
    buildRichText('about-8', t('about-partners-text')),
    buildRichText('about-9', t('about-trust-title'), 'h2'),
    buildRichText('about-10', t('about-trust-text-1')),
    buildRichText('about-11', t('about-trust-text-2')),
    buildRichText('about-12', t('about-contact-title'), 'h2'),
    buildRichText('about-13', t('about-contact-text')),
  ]

  return (
    <>
      {/* Hero Banner */}
      <div className="relative h-[480px] xs:mb-10 overflow-hidden">
        <div className="absolute inset-0 z-0 xs:rounded-md overflow-hidden">
          <Image
            src={BANNER_IMAGE}
            alt={`${bannerTitle.name} - SIGHT2SIGHT`}
            title={`${bannerTitle.name} - SIGHT2SIGHT`}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            priority
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1/2 z-20 xs:rounded-md overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t xs:from-black/80 from-black/90 to-transparent"></div>
        </div>
        <div className="mx-3 xs:mx-8 flex md:flex-row flex-col md:items-end items-stretch justify-between gap-3 z-30 mb-6 xs:mb-8 absolute bottom-0 left-0 right-0">
          <div className="flex flex-col items-start text-white">
            <h1 className="text-left xs:text-6xl text-3xl font-medium font-rubik uppercase">
              {bannerTitle.name}
            </h1>
            <div className="text-left xs:text-2xl text-2xl mt-2">
              {bannerTitle.description}
            </div>
          </div>
        </div>
      </div>

      <Description description={content1} withBorder />

      {/* Separator Image */}
      <div className="relative h-[384px] xs:h-[480px] my-10 mx-3 xs:mx-0 rounded-md overflow-hidden">
        <Image
          src={SEPARATOR_IMAGE}
          alt="SIGHT2SIGHT - Tours and Activities"
          title="SIGHT2SIGHT - Tours and Activities"
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
      </div>

      <Description description={content2} withBorder />
    </>
  )
}
